import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { PiCaretLeftLight, PiArrowsInLight, PiXLight } from 'react-icons/pi';
import Header, { Action } from '../TailwindComponents/Header';
import NotFoundPage from '../TailwindComponents/NotFoundPage';
import CollaboratorBadge from '../TailwindComponents/CollaboratorsBadge';

export interface CollaboratorBadge {
  user_uuid: string;
  name: string;
  initials: string;
  access: string;
  email?: string | null;
  avatar_url?: string | null;
}

interface DetailFrameProps {
  children: ReactNode;
  actions?: Action[];
  dropdownActions?: Action[];
  renderDropdownAsButtons?: boolean;
  subscriptionActive?: boolean;
  breadcrumbs: Array<{ name: string; href: string; current: boolean }>;
  showBackButton?: boolean;
  onBack?: () => void;
  additionalHeaderElements?: ReactNode;
  subHeaderRight?: ReactNode;
  className?: string;
  isFullscreen?: boolean;
  onExitFullscreen?: () => void;
  fileInfo?: {
    name: string;
    type?: string;
  };
  collaborators?: CollaboratorBadge[];
  notFound?: boolean;
}

const DetailFrame: React.FC<DetailFrameProps> = ({
  children,
  actions = [],
  dropdownActions = [],
  renderDropdownAsButtons = false,
  subscriptionActive = true,
  breadcrumbs,
  showBackButton = true,
  onBack,
  additionalHeaderElements,
  subHeaderRight,
  className = '',
  isFullscreen = false,
  onExitFullscreen,
  fileInfo,
  collaborators = [],
  notFound = false,
}) => {
  const history = useHistory();
  const { t } = useTranslation();

  const handleBack = onBack || history.goBack;

  const maxVisible = 4;
  const visibleCollabs = collaborators.slice(0, maxVisible);
  const overflowCount = collaborators.length - maxVisible;

  const collaboratorBadges = collaborators.length > 0 ? (
    <div className="flex items-center -space-x-1.5 ml-2">
      {visibleCollabs.map((c) => (
        <div key={c.user_uuid} className="relative">
          <CollaboratorBadge
            collaborator={{
              name: c.name,
              avatarURL: c.avatar_url,
              initials: c.initials,
              email: c.email || null
            }}
          />
        </div>
      ))}
      {overflowCount > 0 && (
        <div className="relative flex items-center justify-center w-7 h-7 rounded-full border-2 border-white dark:border-gray-800 text-[10px] font-medium bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 cursor-default">
          +{overflowCount}
        </div>
      )}
    </div>
  ) : null;

  // If not found, render the NotFoundPage with back button
  if (notFound) {
    return <NotFoundPage />;
  }

  if (isFullscreen && fileInfo) {
    return (
      <div className="fixed inset-0 z-50 bg-white dark:bg-gray-900 overflow-hidden">
        {/* Fullscreen Header */}
        <div className="flex justify-between items-center px-6 py-4 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-4">
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white truncate max-w-md">
                {fileInfo.name}
              </h1>
              {fileInfo.type && (
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {fileInfo.type}
                </p>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={onExitFullscreen}
              className="flex items-center justify-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
              title={t('Exit fullscreen')}
            >
              <PiArrowsInLight className="h-5 w-5" />
            </button>
            <button
              onClick={handleBack}
              className="flex items-center justify-center p-2 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors text-gray-900 dark:text-white"
              title={t('Close')}
            >
              <PiXLight className="h-5 w-5" />
            </button>
          </div>
        </div>

        {/* Fullscreen Content */}
        <div className="flex-1 overflow-hidden h-full">
          <div className={`h-full flex flex-col ${className}`}>
            {children}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col w-full h-full overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-900 flex-shrink-0">
        <Header
          actions={actions}
          dropdownActions={dropdownActions}
          renderDropdownAsButtons={renderDropdownAsButtons}
          subscriptionActive={subscriptionActive}
          additionalRightElements={
            <>
              {additionalHeaderElements}
              {collaboratorBadges}
            </>
          }
          breadcrumbs={breadcrumbs}
          dropdownIcon={dropdownActions.length > 0 ? <span>⋮</span> : undefined}
          dropdownLabel={undefined}
        />
        {/* Back button row - commented out to test layout */}
        {/* <div className="flex justify-between items-center ml-1 pr-4 my-1 flex-shrink-0 relative z-40 border-b border-gray-200 dark:border-gray-700">
          {showBackButton && (
            <button
              onClick={handleBack}
              className="flex pl-0.5 py-1 rounded hover:bg-gray-200 transition-all w-fit items-center text-primary-500 dark:text-primary-300 hover:text-primary-600 dark:hover:text-primary-200 text-sm"
            >
              <PiCaretLeftLight className="h-4 w-4 mr-1" />
            </button>
          )}
          {subHeaderRight && <div className="pr-1">{subHeaderRight}</div>}
        </div> */}
        {/* {subHeaderRight && <div className="pr-4 my-1 flex-shrink-0 relative z-40 border-b border-gray-200 dark:border-gray-700 pb-2">{subHeaderRight}</div>} */}
      </div>
      <div className={`flex-1 overflow-hidden`}>
        <div className={`h-full flex flex-col ${className}`}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default DetailFrame;
